// Module 3.2a — BHA storage layer.
//
// Storage key: rsc.bha.v1
//
// Each record carries `wellId` for ownership. Public list/read helpers always
// scope by wellId so a UI never has to filter manually.
//
// Auto-sequencing is enforced on every create / update — the persisted
// record's `components[i].sequence` is always exactly i.
//
// Side-effect: every create / update / delete also refreshes
// WellProfile.children.bha[] with the lightweight summary projection so the
// well's Status panel can render the BHA count without loading any BHA bulk
// payload.

import {
  emptyBhaRecord, validateBhaRecord,
  resequenceComponents, summariseBha,
} from "./schema";
import {
  getWellProfile, updateWellProfile,
} from "@/lib/well/storage";
import { scoped } from "@/lib/accountScope";

export const BHA_STORAGE_KEY = "rsc.bha.v1";

const _now = () => new Date().toISOString();
const _genId = () => `bha_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const _safeParse = (s, fallback) => { try { return JSON.parse(s) ?? fallback; } catch { return fallback; } };

function _readAll() { return _safeParse(scoped.getItem(BHA_STORAGE_KEY), []); }
function _writeAll(rows) { scoped.setItem(BHA_STORAGE_KEY, JSON.stringify(rows)); }

function _bake(rec) { return resequenceComponents(rec); }
function _validateOrThrow(rec) {
  const v = validateBhaRecord(rec);
  if (!v.ok) { const e = new Error("BHA failed validation."); e.validationErrors = v.errors; throw e; }
}

function _refreshWellSummary(wellId) {
  if (!wellId) return;
  const well = getWellProfile(wellId);
  if (!well) return;
  const allBhas = _readAll().filter((r) => r.wellId === wellId && r.isActive !== false);
  const summary = allBhas.map(summariseBha);
  try { updateWellProfile(wellId, { children: { ...well.children, bha: summary } }); }
  catch { /* best-effort — never block the BHA write because the well summary failed */ }
}

// ---------- Public API ----------------------------------------------------

export function createBha(payload) {
  const all = _readAll();
  const base = { ...emptyBhaRecord(), ...payload };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  const baked = _bake(base);
  _validateOrThrow(baked);
  all.push(baked);
  _writeAll(all);
  _refreshWellSummary(baked.wellId);

  // RC-1 — Active BHA ownership lives on the Well. The first BHA created for
  // a well becomes the active BHA automatically. Subsequent creations DO NOT
  // replace the existing active BHA — see `setActiveBhaForWell` for the
  // explicit promote action.
  if (baked.wellId) {
    const well = getWellProfile(baked.wellId);
    if (well && !well.activeBhaId) {
      try { updateWellProfile(baked.wellId, { activeBhaId: baked.id }); }
      catch { /* best-effort — never block BHA creation on the well update */ }
    }
  }

  return baked;
}

/**
 * RC-1 — Explicit "Set Active BHA" action. Mirrors the Active Mud Program
 * pattern. Promoting a different BHA NEVER happens silently; callers must
 * use this function (typically in response to a UI confirmation).
 */
export function setActiveBhaForWell(wellId, bhaId) {
  if (!wellId) throw new Error("setActiveBhaForWell: wellId required");
  const target = bhaId ? _readAll().find((r) => r.id === bhaId && r.wellId === wellId) : null;
  if (bhaId && !target) {
    throw new Error(`setActiveBhaForWell: BHA ${bhaId} not found on well ${wellId}`);
  }
  return updateWellProfile(wellId, { activeBhaId: bhaId || "" });
}

export function getBha(id) {
  return _readAll().find((r) => r.id === id) || null;
}

/** Newest-updated first. Always scoped to a wellId. */
export function listBhasForWell(wellId, { includeInactive = false } = {}) {
  return _readAll()
    .filter((r) => r.wellId === wellId && (includeInactive || r.isActive !== false))
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

export function updateBha(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`BHA ${id} not found.`);
  const merged = {
    ...all[i],
    ...patch,
    id: all[i].id,
    createdAt: all[i].createdAt,
    wellId: all[i].wellId,  // wellId is immutable
  };
  merged.updatedAt = _now();
  const baked = _bake(merged);
  _validateOrThrow(baked);
  all[i] = baked;
  _writeAll(all);
  _refreshWellSummary(baked.wellId);
  return baked;
}

/**
 * Hard-delete a BHA.
 *
 * Important: does NOT touch any Reference Library record nor any other Well
 * Profile. Only the WellProfile.children.bha summary list is refreshed.
 */
export function deleteBha(id) {
  const all = _readAll();
  const target = all.find((r) => r.id === id);
  if (!target) return 0;
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  _refreshWellSummary(target.wellId);
  return 1;
}

export function _clearAllBhas() { scoped.removeItem(BHA_STORAGE_KEY); }
